// ============================================================
// RedteamResolveModal.jsx
// Resolves a red-team alert for a specific org+control.
// All state is in org_control_status — never touches controls.
//
// Usage in ControlDetail.jsx:
//   <RedteamResolveModal
//     controlId={id}
//     assetId={assetId}
//     onClose={() => setShowResolveModal(false)}
//     onResolved={() => { fetchControl(); fetchRedteamFindings() }}
//   />
// ============================================================

import { useState } from 'react'
import { motion } from 'framer-motion'
import { ShieldCheck, X } from 'lucide-react'
import API from "../services/api.js"

export default function RedteamResolveModal({ controlId, assetId, onClose, onResolved }) {
  const [reason, setReason]         = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  async function handleResolve() {
    if (!reason.trim()) {
      setReasonError(true)
      setTimeout(() => setReasonError(false), 400)
      setError('Please provide a reason for resolving this alert.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await API.post(`/controls/${controlId}/resolve/`, { asset_id: assetId, reason: reason.trim() })

      onResolved?.()
      onClose()
    } catch (err) {
      setError(err.message)
    }

    setSubmitting(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-0 sm:px-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="
          w-full sm:max-w-lg
          rounded-t-2xl sm:rounded-2xl
          bg-gradient-to-b from-white/10 to-white/5
          backdrop-blur-2xl border border-white/10
          shadow-[0_-4px_40px_rgba(0,0,0,0.5)] sm:shadow-[0_10px_40px_rgba(0,0,0,0.6)]
          p-5 sm:p-6 max-h-[92dvh] overflow-y-auto
        "
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4 sm:hidden" />

        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-green-400 shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight">
              Resolve Red-team Alert
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white transition-colors shrink-0">
            <X size={18} />
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-5">
          This marks all open findings for this control as resolved and clears the alert
          for your organisation. Provide a reason so your team understands why the risk
          was accepted or remediated.
        </p>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">
            Reason <span className="text-red-400">*</span>
          </label>
          <motion.div
            animate={reasonError ? { x: [-8, 8, -5, 5, 0] } : {}}
            transition={{ duration: 0.35 }}
          >
            <textarea
              value={reason}
              onChange={e => { setReason(e.target.value); if (error) setError(null) }}
              placeholder="e.g. Mitigating control implemented — rate limiting and output filtering deployed. Risk accepted pending next red-team cycle."
              rows={4}
              maxLength={500}
              className={`
                w-full px-4 py-3 rounded-lg text-sm
                bg-white/5 text-white placeholder-gray-500
                border transition-all duration-200 focus:outline-none resize-none
                ${reasonError
                  ? 'border-red-500 ring-1 ring-red-500'
                  : 'border-white/10 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30'
                }
              `}
            />
          </motion.div>
          <p className="text-[11px] text-gray-600 mt-1 text-right">{reason.length}/500</p>
        </div>

        <div className="mt-4 px-3 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-[11px] text-yellow-300/80 leading-relaxed">
            If a new red-team report is uploaded and this control fails again,
            the alert will be automatically reinstated.
          </p>
        </div>

        {error && (
          <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-red-400 mt-3">
            {error}
          </motion.p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end items-stretch sm:items-center gap-2 sm:gap-3 mt-5 sm:mt-6">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-gray-500/50 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            type="button" onClick={handleResolve} disabled={submitting}
            className="px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ShieldCheck size={15} />
            {submitting ? 'Resolving…' : 'Mark as Resolved'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}